/**
 * 机器人相关常量
 */
import type { Component } from 'vue';
import { ChatDotRound, Document, User, Phone, Picture, List } from '@element-plus/icons-vue';

/** 发布状态 */
export const PUBLISH_STATUS = {
  DRAFT: 1,
  RUNNING: 2,
  OFFLINE: 3,
} as const;

/**
 * 质检报告入口开关。
 *
 * 功能未开发完，先隐藏所有 Agent 卡片「...」菜单里的入口；
 * 弹窗组件与取数逻辑原样保留，开发完成后改回 true 即可恢复。
 * 显式标注 boolean（不用 as const），避免被窄化成字面量 false 后
 * 模板里的 && 判断被当成恒假表达式。
 */
export const QUALITY_REPORT_ENABLED: boolean = false;

/**
 * 唤醒语「复制」跳新建落地页时携带的来源消息组 ID 查询参数。
 * 落地页据此拉原记录预填内容，仍按新建提交。
 */
export const WAKE_COPY_FROM_QUERY = 'copy_from';

/**
 * Agent 类型（对应 /robots 接口的 agent_type 参数）。
 *
 * 同一套 /robots 接口承载多种智能体，靠这个字段区分。
 * 后续新增 Agent 类型在此追加，对应页面传各自的常量即可，API 层无需改动。
 */
export const AGENT_TYPE = {
  /** 线索 Agent */
  CLUE: 1,
  /** 企微 Agent */
  WECOM: 2,
  /** 直播 Agent */
  LIVE: 3,
  /** 导购 Agent */
  GUIDE: 4,
} as const;

/** 消息内容最大长度 */
export const MESSAGE_MAX_LENGTH = 200;

/** 延迟最大秒数（1 小时） */
export const DELAY_MAX_SECONDS = 3600;

/** 高级引导语「未开口发送」延时上限（小时） */
export const ADVANCED_DELAY_MAX_HOURS = 72;

/** 高级引导语「未开口发送」默认延时（小时） */
export const ADVANCED_DELAY_DEFAULT_HOURS = 24;

/** 免打扰默认配置 - 未留资用户 */
export const DEFAULT_DO_NOT_DISTURB_UNLEAD = { start: '22:30', end: '08:00' } as const;

/** 免打扰默认配置 - 已留资用户 */
export const DEFAULT_DO_NOT_DISTURB_LEADED = { start: '23:00', end: '07:30' } as const;

/** 启用/停用状态 */
export const STATUS_ENABLED = 1;
export const STATUS_DISABLED = 0;

/** 流程编辑器画布常量 */
export const FLOW_CARD_W = 224;
export const FLOW_CARD_H = 120;
export const FLOW_CANVAS_PAD = 48;
export const FLOW_DEFAULT_NODE_X = 400;
export const FLOW_DEFAULT_NODE_Y_OFFSET = 100;
export const FLOW_NODE_VERTICAL_GAP = 200;
export const FLOW_MIN_WIDTH = 800;
export const FLOW_MIN_HEIGHT = 400;

/** 消息类型选项（Greeting、Wake 共用） */
export const MESSAGE_TYPES: Array<{ type: string; title: string; icon: Component }> = [
  { type: 'text', title: '文本', icon: ChatDotRound },
  { type: 'leadCard', title: '留资卡', icon: Document },
  { type: 'wechatCard', title: '微信名片', icon: User },
  { type: 'collectPhone', title: '收集号码', icon: Phone },
  { type: 'image', title: '图片', icon: Picture },
  { type: 'qaMenu', title: '问答菜单', icon: List },
];

/** 配置版本：0=线上发布版，1=草稿版 */
export const VERSION_TYPE = {
  ONLINE: 0,
  DRAFT: 1,
} as const;

/** 有操作时的续期节流间隔（5 分钟内最多续一次） */
export const DRAFT_ACTIVE_RENEW_INTERVAL_MS = 5 * 60 * 1000;

/**
 * 拟人化分段回复模式（对应后端 segmented_reply_switch，原为 0/1 开关，现扩展为三态）
 */
export const SEGMENTED_REPLY_MODE = {
  /** 不开启：每次回复合并为一条消息发送 */
  OFF: 0,
  /** AI 按内容长短与语义自动拆分为多条消息 */
  AUTO: 1,
  /** AI 不自动分段，严格按文案中自行添加的 \n 换行 */
  MANUAL_BREAK: 2,
} as const;

/** 选项按产品要求的展示顺序排列（不开启 → 仅按 \n 换行 → 自动分段），与取值大小无关 */
export const SEGMENTED_REPLY_MODE_OPTIONS = [
  { value: SEGMENTED_REPLY_MODE.OFF, label: '不开启' },
  {
    value: SEGMENTED_REPLY_MODE.MANUAL_BREAK,
    label: 'AI不会自动分段，严格按照自添加的\\n自动换行',
  },
  {
    value: SEGMENTED_REPLY_MODE.AUTO,
    label:
      'AI 会根据回复内容的长短与语义，自动将一段话拆分为多条消息依次发送，同时可自添加\\n规范换行',
  },
];
